import { Button } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import EvFilterSection from '../EvFilterSection';
import styles from './EvFiltersPanel.module.css';

const cx = classnames.bind(styles);

// Payment method options (maps to cards_accepted API field)
// "Select All" checkbox handles the all-selected state; no separate "All" option needed
const PAYMENT_METHOD_OPTIONS = [
  { value: 'pwgb', label: 'Pay with GasBuddy+\u2122' },
  { value: 'cash', label: 'Cash' },
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
  { value: 'apple_pay', label: 'Apple Pay' },
  { value: 'android_pay', label: 'Android Pay' },
];

// Default charger type options
const CHARGER_TYPE_OPTIONS = [
  { value: 'DCFast', label: 'DC Fast' },
  { value: 'Level2', label: 'Level 2' },
  { value: 'Level1', label: 'Level 1' },
];

// Default connector type options
// API values: J1772, J1772COMBO (CCS), CHADEMO, TESLA, NEMA1450, NEMA515, NEMA520, J3271
const CONNECTOR_TYPE_OPTIONS = [
  { value: 'J1772', label: 'J1772' },
  { value: 'J1772COMBO', label: 'CCS' },
  { value: 'CHADEMO', label: 'CHAdeMO' },
  { value: 'TESLA', label: 'NACS/Tesla' },
];

// Default access type options
const ACCESS_TYPE_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
];

/**
 * EvFiltersPanel - Filter panel for EV charging stations
 *
 * Filter logic:
 * - OR within filter categories (e.g., Level 2 OR DC Fast)
 * - AND across categories (e.g., Level 2 AND CCS connector)
 */
export default function EvFiltersPanel({
  networks,
  filters,
  onFiltersChange,
  onReset,
  isCollapsed,
  onToggleCollapse,
}) {
  // Track which sections are expanded - payment expanded by default
  const [expandedSections, setExpandedSections] = useState({
    payment: true,
    chargerType: false,
    connectors: false,
    networks: false,
    access: false,
  });

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleChargerTypeChange = useCallback((values) => {
    onFiltersChange({ ...filters, chargerTypes: values });
  }, [filters, onFiltersChange]);

  const handleConnectorChange = useCallback((values) => {
    onFiltersChange({ ...filters, connectorTypes: values });
  }, [filters, onFiltersChange]);

  const handleNetworkChange = useCallback((values) => {
    onFiltersChange({ ...filters, networks: values });
  }, [filters, onFiltersChange]);

  const handleAccessChange = useCallback((values) => {
    onFiltersChange({ ...filters, accessTypes: values });
  }, [filters, onFiltersChange]);

  const handlePaymentChange = useCallback((values) => {
    onFiltersChange({ ...filters, paymentMethods: values });
  }, [filters, onFiltersChange]);

  // Convert networks prop to filter options format
  const networkOptions = useMemo(() => {
    if (!networks || networks.length === 0) {
      return [];
    }
    return networks.map(network => ({
      value: network.networkKey || network.id,
      label: network.name,
    }));
  }, [networks]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => (
    (filters.paymentMethods?.length > 0)
    || (filters.chargerTypes?.length > 0)
    || (filters.connectorTypes?.length > 0)
    || (filters.networks?.length > 0)
    || (filters.accessTypes?.length > 0)
  ), [filters]);

  const activeFilterCount = useMemo(() => (
    (filters.paymentMethods?.length || 0)
    + (filters.chargerTypes?.length || 0)
    + (filters.connectorTypes?.length || 0)
    + (filters.networks?.length || 0)
    + (filters.accessTypes?.length || 0)
  ), [filters]);

  if (isCollapsed) {
    return (
      <div className={cx('filtersCollapsed')}>
        <button
          type="button"
          className={cx('expandButton')}
          onClick={onToggleCollapse}
        >
          Filters
          {activeFilterCount > 0 && (
            <span className={cx('filterBadge')}>{activeFilterCount}</span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={cx('filtersPanel')}>
      <div className={cx('filtersHeader')}>
        <h3 className={cx('filtersTitle')}>Filters</h3>
        {onToggleCollapse && (
          <button
            type="button"
            className={cx('collapseButton')}
            onClick={onToggleCollapse}
            aria-label="Collapse filters"
          >
            &times;
          </button>
        )}
      </div>

      <div className={cx('filtersSections')}>
        <EvFilterSection
          title="Payment Methods"
          options={PAYMENT_METHOD_OPTIONS}
          selectedValues={filters.paymentMethods || []}
          onChange={handlePaymentChange}
          isExpanded={expandedSections.payment}
          onToggle={() => toggleSection('payment')}
        />

        <EvFilterSection
          title="Charger Type"
          options={CHARGER_TYPE_OPTIONS}
          selectedValues={filters.chargerTypes || []}
          onChange={handleChargerTypeChange}
          isExpanded={expandedSections.chargerType}
          onToggle={() => toggleSection('chargerType')}
        />

        <EvFilterSection
          title="Connector Type"
          options={CONNECTOR_TYPE_OPTIONS}
          selectedValues={filters.connectorTypes || []}
          onChange={handleConnectorChange}
          isExpanded={expandedSections.connectors}
          onToggle={() => toggleSection('connectors')}
        />

        {networkOptions.length > 0 && (
          <EvFilterSection
            title="Network"
            options={networkOptions}
            selectedValues={filters.networks || []}
            onChange={handleNetworkChange}
            isExpanded={expandedSections.networks}
            onToggle={() => toggleSection('networks')}
          />
        )}

        <EvFilterSection
          title="Access Type"
          options={ACCESS_TYPE_OPTIONS}
          selectedValues={filters.accessTypes || []}
          onChange={handleAccessChange}
          isExpanded={expandedSections.access}
          onToggle={() => toggleSection('access')}
        />
      </div>

      {hasActiveFilters && (
        <div className={cx('filtersFooter')}>
          <Button
            size="sm"
            onClick={onReset}
            tertiary
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
}

EvFiltersPanel.propTypes = {
  networks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    networkKey: PropTypes.string,
    name: PropTypes.string.isRequired,
  })),
  filters: PropTypes.shape({
    paymentMethods: PropTypes.arrayOf(PropTypes.string),
    chargerTypes: PropTypes.arrayOf(PropTypes.string),
    connectorTypes: PropTypes.arrayOf(PropTypes.string),
    networks: PropTypes.arrayOf(PropTypes.string),
    accessTypes: PropTypes.arrayOf(PropTypes.string),
  }),
  onFiltersChange: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

EvFiltersPanel.defaultProps = {
  networks: [],
  filters: {},
  onReset: undefined,
  isCollapsed: false,
  onToggleCollapse: undefined,
};
